/**
 * QA Formatting Panel
 * 
 * Displays formatting verification results with visual indicators,
 * requirement comparison, and fix recommendations.
 */

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { verifyFormatting, type FormattingValidationResult } from '../../api/qaApi';

export interface QAFormattingPanelProps {
  reportId: string;
}

export function QAFormattingPanel({ reportId }: QAFormattingPanelProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-formatting', reportId],
    queryFn: () => verifyFormatting(reportId),
    enabled: false, // Don't auto-fetch, require manual trigger
    retry: false
  });
  
  const verifyMutation = useMutation({
    mutationFn: () => verifyFormatting(reportId),
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      console.error('[QA Formatting] Verification failed:', error);
      alert(`Formatting verification failed: ${error.message || 'Unknown error'}`);
    }
  });
  
  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await verifyMutation.mutateAsync();
    } finally {
      setIsVerifying(false);
    }
  };
  
  if (!data && !isLoading && !error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Click "Verify Formatting" to check formatting against RFP requirements
          </p>
          <Button
            onClick={handleVerify}
            disabled={isVerifying}
            leftIcon={isVerifying ? <LoadingSpinner /> : <RefreshCw className="w-4 h-4" />}
          >
            {isVerifying ? 'Verifying...' : 'Verify Formatting'}
          </Button>
        </div>
      </Card>
    );
  }
  
  if (isLoading || isVerifying) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Verifying formatting...</span>
        </div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">
            Failed to verify formatting: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button onClick={handleVerify} variant="outline">
            Retry
          </Button>
        </div>
      </Card>
    );
  }
  
  if (!data || data.error) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-yellow-400 mb-4" />
          <p className="text-yellow-600 dark:text-yellow-400 mb-4">
            {data?.error || 'Formatting verification not available'}
          </p>
          <Button onClick={handleVerify} variant="outline">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }
  
  const results: FormattingValidationResult = data;
  
  return (
    <Card>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Formatting Verification</h3>
          <div className="flex items-center gap-2">
            <Badge variant={results.overall_passed ? 'success' : 'error'}>
              {results.overall_passed ? 'Passed' : 'Failed'}
            </Badge>
            {results.validated_at && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Verified: {new Date(results.validated_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <Button
          onClick={handleVerify}
          disabled={isVerifying}
          variant="outline"
          size="sm"
          leftIcon={isVerifying ? <LoadingSpinner /> : <RefreshCw className="w-4 h-4" />}
        >
          {isVerifying ? 'Verifying...' : 'Re-verify'}
        </Button>
      </div>
      
      <div className="space-y-4">
        {Object.entries(results.requirements).map(([key, requirement]) => {
          if (!requirement) return null;
          
          const passed = requirement.passed !== false;
          const Icon = passed ? CheckCircle : XCircle;
          const iconColor = passed ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
          
          return (
            <div
              key={key}
              className={`p-4 rounded-lg border ${
                passed
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-theme-text capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <Badge variant={passed ? 'success' : 'error'} size="sm">
                      {passed ? 'Passed' : 'Failed'}
                    </Badge>
                  </div>
                  
                  {requirement.issues && requirement.issues.length > 0 && (
                    <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                      {requirement.issues.map((issue, idx) => (
                        <li key={idx}>{issue}</li>
                      ))}
                    </ul>
                  )}
                  
                  {requirement.current !== undefined && requirement.limit !== undefined && (
                    <div className="mt-2 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Word count: <strong>{requirement.current.toLocaleString()}</strong> / {requirement.limit.toLocaleString()}
                      </span>
                      {requirement.exceeded_by && (
                        <span className="text-red-600 dark:text-red-400 ml-2">
                          (Exceeded by {requirement.exceeded_by.toLocaleString()} words)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {results.fix_recommendations && results.fix_recommendations.length > 0 && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <h4 className="font-medium text-theme-text">Fix Recommendations</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {results.fix_recommendations.map((rec, idx) => (
              <li key={idx}>{rec}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
